import React, { useState, useEffect } from 'react';
import { claimAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { DollarSign, AlertCircle, Eye, Edit, MessageSquare } from 'lucide-react';

const Claims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadClaims();
  }, []);

  const loadClaims = async () => {
    try {
      const res = await claimAPI.getClaims();
      setClaims(res.data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewClaimDetails = (claim) => {
    setSelectedClaim(claim);
    setDetailsOpen(true);
  };

  const filteredClaims = claims.filter((c) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'action_needed') return c.status === 'revision_requested';
    return c.status === activeTab;
  });

  const totalPending = claims.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
  const totalPaid = claims.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount, 0);
  const revisionCount = claims.filter(c => c.status === 'revision_requested').length;

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      revision_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      submitted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      under_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pic_approved: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      admin_approved: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    };
    const labels = {
      paid: 'Paid',
      pending: 'Pending',
      rejected: 'Rejected',
      revision_requested: 'Needs Revision',
      approved: 'Fully Approved',
      draft: 'Draft',
      submitted: 'Submitted',
      under_review: 'Under Review',
      pic_approved: 'PIC Approved',
      admin_approved: 'Admin Approved',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-gray-500/20 text-gray-400'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="claims-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Commission Claims</h1>
        <p className="text-slate-400 mt-1">Track your commission earnings</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <GlassCard className="text-center">
          <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-emerald-400 font-mono">{formatCurrency(totalPaid)}</p>
          <p className="text-sm text-slate-400">Total Earned</p>
        </GlassCard>
        <GlassCard className="text-center">
          <DollarSign className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-amber-400 font-mono">{formatCurrency(totalPending)}</p>
          <p className="text-sm text-slate-400">Pending Claims</p>
        </GlassCard>
        <GlassCard className="text-center">
          <DollarSign className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white font-mono">{claims.length}</p>
          <p className="text-sm text-slate-400">Total Claims</p>
        </GlassCard>
        {revisionCount > 0 && (
          <GlassCard className="text-center border-orange-500/30 bg-orange-500/10">
            <AlertCircle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-orange-400 font-mono">{revisionCount}</p>
            <p className="text-sm text-orange-300">Needs Revision</p>
          </GlassCard>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          {revisionCount > 0 && (
            <TabsTrigger value="action_needed" className="text-orange-400">
              Action Needed ({revisionCount})
            </TabsTrigger>
          )}
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredClaims.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Claim Type</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClaims.map((claim) => (
                    <TableRow key={claim.id} className="border-slate-700">
                      <TableCell className="text-white font-medium capitalize">
                        {claim.claim_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="text-emerald-400 font-mono font-semibold">
                        {formatCurrency(claim.amount)}
                      </TableCell>
                      <TableCell>{getStatusBadge(claim.status)}</TableCell>
                      <TableCell className="text-slate-400">{formatDate(claim.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewClaimDetails(claim)}
                            className="text-blue-400 border-blue-400/30 hover:bg-blue-500/10"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {claim.status === 'revision_requested' && (
                            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Action Required
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <DollarSign className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Claims</h3>
              <p className="text-slate-400">
                {activeTab === 'all' ? 'You have no claims yet' : `No ${activeTab} claims`}
              </p>
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>

      {/* Claim Details Sheet */}
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-slate-900 border-slate-700">
          {selectedClaim && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-400" />
                  Claim Details
                </SheetTitle>
                <SheetDescription className="text-slate-400">
                  {selectedClaim.claim_type?.replace(/_/g, ' ')} claim
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status and Amount */}
                <div className="flex items-center justify-between">
                  {getStatusBadge(selectedClaim.status)}
                  <span className="text-2xl font-bold text-emerald-400">{formatCurrency(selectedClaim.amount)}</span>
                </div>

                {/* Revision Request Alert */}
                {selectedClaim.status === 'revision_requested' && (
                  <Card className="bg-orange-500/10 border-orange-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-orange-400">
                        <MessageSquare className="h-4 w-4" />
                        Revision Required
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-orange-300">
                        Your claim needs revision. Please review the comments below and update your claim.
                      </p>
                      {selectedClaim.revision_comment && (
                        <div className="p-3 bg-slate-800 rounded-lg border border-orange-500/20">
                          <p className="text-sm font-medium text-orange-400 mb-1">Reviewer's Comments:</p>
                          <p className="text-sm text-slate-300">{selectedClaim.revision_comment}</p>
                        </div>
                      )}
                      {selectedClaim.revision_requested_by_name && (
                        <p className="text-xs text-slate-500">
                          Requested by {selectedClaim.revision_requested_by_name} ({selectedClaim.revision_requested_by_role?.toUpperCase()}) 
                          on {formatDate(selectedClaim.revision_requested_at)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Claim Info */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Claim Type</p>
                      <p className="text-white capitalize">{selectedClaim.claim_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Booking ID</p>
                      <p className="text-white font-mono text-xs">{selectedClaim.booking_id?.slice(0, 12)}...</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Created</p>
                      <p className="text-white">{formatDate(selectedClaim.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Amount</p>
                      <p className="text-emerald-400 font-semibold">{formatCurrency(selectedClaim.amount)}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Rejection Reason */}
                {selectedClaim.status === 'rejected' && selectedClaim.rejection_reason && (
                  <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium text-red-400 mb-1">Rejection Reason:</p>
                      <p className="text-sm text-slate-300">{selectedClaim.rejection_reason}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Claims;
